import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { settingsService } from '../../../lib/services';

const CustomScriptsSettings = () => {
  const [headScripts, setHeadScripts] = useState('');
  const [bodyScripts, setBodyScripts] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await settingsService?.getByCategory('scripts');
        if (data?.length) {
          data?.forEach(s => {
            if (s?.settingKey === 'custom_scripts_head') setHeadScripts(s?.settingValue || '');
            if (s?.settingKey === 'custom_scripts_body') setBodyScripts(s?.settingValue || '');
          });
        }
      } catch (e) {
        console.error('Failed to load script settings:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError('');
    try {
      const { error } = await settingsService?.upsert([
        { settingKey: 'custom_scripts_head', settingValue: headScripts, settingType: 'text', category: 'scripts' },
        { settingKey: 'custom_scripts_body', settingValue: bodyScripts, settingType: 'text', category: 'scripts' },
      ]);
      if (error) throw error;
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err?.message || 'Failed to save script settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={24} className="text-primary animate-spin mr-2" />
        <span className="text-muted-foreground">Loading script settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {saveSuccess && (
        <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-lg text-success text-sm">
          <Icon name="CheckCircle" size={16} />
          Custom scripts saved successfully!
        </div>
      )}
      {saveError && (
        <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
          <Icon name="AlertCircle" size={16} />
          {saveError}
        </div>
      )}
      <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={18} className="text-warning flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground mb-1">Security Notice</p>
            <p className="text-xs text-muted-foreground">
              Only paste scripts from trusted sources. Malicious scripts can compromise your site and user data. 
              Scripts are injected as-is into your site's HTML.
            </p>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-5">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Code" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground mb-1">Head Scripts</h3>
            <p className="text-sm text-muted-foreground">
              Injected inside <code className="bg-muted px-1 py-0.5 rounded text-xs">&lt;head&gt;</code> — ideal for Google Analytics, GTM, fonts, or meta pixels
            </p>
          </div>
        </div>
        <textarea
          value={headScripts}
          onChange={e => setHeadScripts(e?.target?.value)}
          rows={8}
          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground font-mono placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 resize-y"
          placeholder={`<!-- Google Analytics -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=GA_MEASUREMENT_ID"></script>\n<script>\n  window.dataLayer = window.dataLayer || [];\n  function gtag(){dataLayer.push(arguments);}\n  gtag('js', new Date());\n  gtag('config', 'GA_MEASUREMENT_ID');\n</script>`}
          spellCheck={false}
        />
      </div>
      <div className="bg-card rounded-lg border border-border p-5">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
            <Icon name="FileCode" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground mb-1">Body Scripts</h3>
            <p className="text-sm text-muted-foreground">
              Injected before <code className="bg-muted px-1 py-0.5 rounded text-xs">&lt;/body&gt;</code> — ideal for chat widgets, heatmaps, or deferred scripts
            </p>
          </div>
        </div>
        <textarea
          value={bodyScripts}
          onChange={e => setBodyScripts(e?.target?.value)}
          rows={8}
          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground font-mono placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 resize-y"
          placeholder={`<!-- Google Tag Manager (noscript) -->\n<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=GTM-XXXXXXX"\nheight="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>`}
          spellCheck={false}
        />
      </div>
      <div className="bg-card rounded-lg border border-border p-5">
        <h3 className="text-sm font-semibold text-foreground mb-3">Common Script Examples</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { name: 'Google Analytics 4', icon: 'BarChart3', location: 'Head', desc: 'Track page views and events' },
            { name: 'Google Tag Manager', icon: 'Tag', location: 'Head + Body', desc: 'Manage all your tags in one place' },
            { name: 'Meta Pixel', icon: 'Facebook', location: 'Head', desc: 'Facebook/Instagram ad tracking' },
            { name: 'Hotjar / Heatmaps', icon: 'MousePointer', location: 'Head', desc: 'User behaviour analytics' },
            { name: 'Intercom / Chat', icon: 'MessageCircle', location: 'Body', desc: 'Customer support chat widget' },
            { name: 'Crisp / Tawk.to', icon: 'MessageSquare', location: 'Body', desc: 'Live chat support' },
          ]?.map((item, i) => (
            <div key={i} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name={item?.icon} size={14} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{item?.name}</p>
                <p className="text-xs text-muted-foreground">{item?.desc}</p>
              </div>
              <span className="text-xs bg-muted px-2 py-0.5 rounded text-muted-foreground flex-shrink-0">{item?.location}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => window.location?.reload()}>Cancel</Button>
        <Button variant="default" loading={saving} iconName="Save" iconPosition="left" onClick={handleSave}>
          Save Scripts
        </Button>
      </div>
    </div>
  );
};

export default CustomScriptsSettings;
